import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { transformResponseHandler } from 'utils/transformResponseHandler';
import { tablesApis } from 'pages/tables/services';

export const orderApis = createApi({
  reducerPath: 'orderApis',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_ROUTE}`,
    prepareHeaders: (headers, { getState }) => {
      // Extract token from state
      const token = getState()?.posData?.token;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getOrders: builder.query({
      query: ({ page, limit, order_type, search }) => ({
        url: '/order',
        method: 'GET',
        params: { page, perPage: limit, order_type, search }
      }),
      providesTags: ['orders'],
      //   transformResponse: (resp) => transformResponseHandler(resp),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    getOrderDetails: builder.query({
      query: (id) => ({
        url: `/order/${id}`,
        method: 'GET',
      }),
      providesTags: ['ordersDetails'],
      //   transformResponse: (resp) => transformResponseHandler(resp),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    changeOrderStatus: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/change-status/${id}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tablesApis.util.invalidateTags(['vendorTables']));
        } catch (err) {
          // do nothing
        }
      },
    }),
    createTableOrder: builder.mutation({
      query: (body) => ({
        url: `/order/table-order`,
        method: 'POST',
        body: body
      }),
      transformErrorResponse: (error) => transformResponseHandler(error),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tablesApis.util.invalidateTags(['vendorTables']));
        } catch (err) {
          // do nothing
        }
      },
    }),
    addOrderItems: builder.mutation({
      query: ({ id, orderData }) => ({
        url: `/order/add-order-item/${id}`,
        method: 'PUT',
        body: orderData
      }),
      invalidatesTags: ['ordersDetails'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    addDiscountOrder: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/discount/${id}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['ordersDetails'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    removeDiscountOrder: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/remove-discount/${id}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['ordersDetails'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    collectOrderPayment: builder.mutation({
      query: (body) => ({
        url: `/order/collectPayment`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tablesApis.util.invalidateTags(['vendorTables']));
        } catch (err) {
          // do nothing
        }
      },
    }),
    completeOrderPayment: builder.mutation({
      query: (body) => ({
        url: `/order/completeOrderPayment`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tablesApis.util.invalidateTags(['vendorTables']));
        } catch (err) {
          // do nothing
        }
      },
    }),
    updateOrder: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/${id}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['ordersDetails'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    getPrintReceipt: builder.query({
      query: (arg) => {
        const id = typeof arg === 'object' && arg !== null ? arg.id : arg;
        const params = typeof arg === 'object' && arg !== null ? { ...arg } : {};
        delete params.id;
        return {
          url: `/order/print-receipt/${id}`,
          method: 'GET',
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    managePackageCharges: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/manage-package-charges/${id}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['ordersDetails'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    splitOrderBill: builder.mutation({
      query: ({ id, body }) => ({
        url: `/order/split-bill/${id}`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    paySplitBill: builder.mutation({
      query: (body) => ({
        url: `/order/pay-split-bill`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    revertSplitPayment: builder.mutation({
      query: (body) => ({
        url: `/order/revert-split-payment`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    removeSplitBill: builder.mutation({
      query: (body) => ({
        url: `/order/remove-split-bill`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['ordersDetails', 'orders'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderDetailsQuery,
  useChangeOrderStatusMutation,
  useCreateTableOrderMutation,
  useAddOrderItemsMutation,
  useAddDiscountOrderMutation,
  useRemoveDiscountOrderMutation,
  useCollectOrderPaymentMutation,
  useCompleteOrderPaymentMutation,
  useUpdateOrderMutation,
  useLazyGetPrintReceiptQuery,
  useManagePackageChargesMutation,
  useSplitOrderBillMutation,
  usePaySplitBillMutation,
  useRevertSplitPaymentMutation,
  useRemoveSplitBillMutation
} = orderApis;
